const { SUPPORTED_PLATFORMS } = require("../../constants")
const { logger, exceptionHandler, callbackParser } = require("../../utils")
const { isSSR, isWebView, getPlatform, getPlatformPrefix } = require("../../utils/platform")

/**
 * Native AppCallbacks handler
 * @param {string} interfaceName - name of the interface called
 * @param {string} methodName - name of the method defined within the interface called
 * @param {object|string|boolean} [args] - arguments passed inside the callback
 * @param {string} [resolve] - success callback handler, format - "interfaceName.methodName"
 * @param {string} [reject] - failure callback handler, format - "interfaceName.methodName"
 * @protected
 */
const callbackHandler = (interfaceName = "", methodName = "", args = {}, resolve = "", reject = "") => {
    if (!isWebView())
        return logger.info(
            `AppCallback - "${interfaceName}.${methodName}" is being called in env other than webview`
        )

    const platform = getPlatform()
    const platformPrefix = getPlatformPrefix()
    const payload = { type: methodName, data: args }
    const successCallback = callbackParser(resolve)
    const failureCallback = callbackParser(reject)
    if (successCallback) payload["resolve"] = successCallback
    if (failureCallback) payload["reject"] = failureCallback

    if (platform === SUPPORTED_PLATFORMS.ANDROID) {
        if (typeof platformPrefix[interfaceName] === "function") {
            return platformPrefix[interfaceName](JSON.stringify(payload))
        } else {
            return logger.error(
                `AppCallback - "${interfaceName}.${methodName}" is not defined in the current scope`
            )
        }
    } else if (platform === SUPPORTED_PLATFORMS.IOS) {
        if (
            platformPrefix[interfaceName] &&
            typeof platformPrefix[interfaceName].postMessage === "function"
        ) {
            return platformPrefix[interfaceName].postMessage(JSON.stringify(payload))
        } else {
            return logger.error(
                `AppCallback - "${interfaceName}.${methodName}" is not defined in the current scope`
            )
        }
    } else {
        return logger.error(`"${platform}" platform is not supported`)
    }
}

/**
 * JS Proxy based method handler
 * @protected
 */
const methodHandler = {
    get: (target, prop) => {
        return exceptionHandler((args, resolve, reject) =>
            callbackHandler(target.interfaceName, prop, args, resolve, reject)
        )
    },
}

/**
 * JS Proxy based interface handler
 * @protected
 */
const interfaceHandler = {
    get: (_, prop) => {
        return new Proxy({ interfaceName: prop }, methodHandler)
    },
    ownKeys() {
        const platfromPrefix = getPlatformPrefix()
        const nativeEvents = Object.keys(platfromPrefix)
        return nativeEvents
    },
    getOwnPropertyDescriptor() {
        return {
            enumerable: true,
            configurable: true,
        }
    },
}

/**
 * Native Bridge Definition
 */
const nativeBridge = {
    /**
     * initializes native bridge
     * @param {object} config
     */
    initialize: (config = {}) => {
        if (isSSR()) return
        window.synapse[config.bridgeName] = new Proxy(
            window.synapse[config.bridgeName] || {},
            interfaceHandler
        )
    },
}

module.exports = nativeBridge
